import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const { FiX, FiStar, FiCheck, FiRefreshCw, FiClock, FiTrendingUp } = FiIcons;

export default function MealModal({ date, meal, onClose, onSave }) {
  const { state } = useFamilyContext();
  const { mealPlans } = state;

  const [formData, setFormData] = useState({
    date: '',
    mealType: 'Abendessen',
    dish: '',
    recipeUrl: '',
    ingredients: '',
    notes: '',
    isFavorite: false
  });

  const [uniqueDishes, setUniqueDishes] = useState([]);
  const [autoFilled, setAutoFilled] = useState(false);
  const [currentStats, setCurrentStats] = useState(null);

  // Lade bekannte Gerichte für Autocomplete & Stats
  useEffect(() => {
    const statsMap = new Map();
    
    // 1. Aggregieren
    mealPlans.forEach(p => {
        if (!p.dish) return;
        const key = p.dish.toLowerCase();
        
        const existing = statsMap.get(key) || { 
            name: p.dish, 
            count: 0, 
            lastDate: null,
            ingredients: p.ingredients,
            recipeUrl: p.recipeUrl
        };
        
        existing.count += 1;
        
        // Finde das aktuellste Datum
        if (p.date) {
            const currentDate = new Date(p.date);
            if (!existing.lastDate || currentDate > existing.lastDate) {
                existing.lastDate = currentDate;
                // Update ingredients/recipe from most recent entry
                if (p.ingredients) existing.ingredients = p.ingredients;
                if (p.recipeUrl) existing.recipeUrl = p.recipeUrl;
            }
        }
        
        statsMap.set(key, existing);
    });

    setUniqueDishes(Array.from(statsMap.values()).sort((a,b) => a.name.localeCompare(b.name)));
  }, [mealPlans]);

  // Initialisiere Formular
  useEffect(() => {
    if (meal && meal.id) {
      setFormData({
        date: meal.date,
        mealType: meal.mealType,
        dish: meal.dish,
        recipeUrl: meal.recipeUrl || '',
        ingredients: meal.ingredients || '',
        notes: meal.notes || '',
        isFavorite: meal.isFavorite || false
      });
      // Set stats for existing meal
      updateStats(meal.dish);
    } else {
      setFormData(prev => ({
        ...prev,
        date: date ? date.toISOString() : new Date().toISOString(),
        mealType: meal?.mealType || 'Abendessen',
        dish: '',
        ingredients: '',
        recipeUrl: '',
        notes: '',
        isFavorite: false
      }));
      setCurrentStats(null);
    }
  }, [meal, date]);

  const updateStats = (dishName) => {
      if (!dishName) {
          setCurrentStats(null);
          return;
      }
      const stats = uniqueDishes.find(d => d.name.toLowerCase() === dishName.toLowerCase());
      setCurrentStats(stats || null);
  };

  // Autocomplete Handling
  const handleDishInput = (e) => {
    const val = e.target.value;
    updateStats(val);
    
    setFormData(prev => {
        const newState = { ...prev, dish: val };
        
        const history = uniqueDishes.find(d => d.name.toLowerCase() === val.toLowerCase());
        
        if (history && history.ingredients && !prev.ingredients) {
             newState.ingredients = history.ingredients;
             if (history.recipeUrl && !prev.recipeUrl) {
                 newState.recipeUrl = history.recipeUrl;
             }
             setAutoFilled(true);
             setTimeout(() => setAutoFilled(false), 3000);
        }
        
        return newState;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {meal && meal.id ? 'Gericht bearbeiten' : 'Gericht planen'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gericht Name *</label>
              <div className="relative">
                <input
                    type="text"
                    list="dish-suggestions"
                    value={formData.dish}
                    onChange={handleDishInput}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="z.B. Spaghetti Bolognese"
                    required
                    autoComplete="off"
                />
                <AnimatePresence>
                    {autoFilled && (
                        <motion.div 
                            initial={{ opacity: 0, x: 10 }} 
                            animate={{ opacity: 1, x: 0 }} 
                            exit={{ opacity: 0 }}
                            className="absolute right-2 top-2 text-green-600 flex items-center text-xs font-medium bg-green-50 px-2 py-0.5 rounded"
                        >
                            <SafeIcon icon={FiCheck} className="w-3 h-3 mr-1" />
                            Gefüllt
                        </motion.div>
                    )}
                </AnimatePresence>
              </div>
              <datalist id="dish-suggestions">
                {uniqueDishes.map((d, i) => (
                    <option key={i} value={d.name} />
                ))}
              </datalist>
              
              {/* Stats Display */}
              {currentStats && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-2 flex items-center gap-4 text-xs text-blue-600 bg-blue-50 p-2 rounded-lg border border-blue-100"
                  >
                      <div className="flex items-center">
                          <SafeIcon icon={FiTrendingUp} className="w-3 h-3 mr-1" />
                          <span>Gesamt: <strong>{currentStats.count}x</strong></span>
                      </div>
                      {currentStats.lastDate && (
                          <div className="flex items-center">
                              <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                              <span>Zuletzt: <strong>{format(currentStats.lastDate, 'dd.MM.yy')}</strong></span>
                          </div>
                      )}
                  </motion.div>
              )}
            </div>

            <div className="flex justify-between gap-4">
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mahlzeit</label>
                    <select
                        value={formData.mealType}
                        onChange={e => setFormData({ ...formData, mealType: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                        <option value="Frühstück">Frühstück</option>
                        <option value="Mittagessen">Mittagessen</option>
                        <option value="Abendessen">Abendessen</option>
                        <option value="Snack">Snack</option>
                    </select>
                </div>
                <div className="flex items-end pb-2">
                    <label className="flex items-center space-x-2 cursor-pointer select-none">
                        <input 
                            type="checkbox" 
                            checked={formData.isFavorite} 
                            onChange={e => setFormData({...formData, isFavorite: e.target.checked})}
                            className="hidden"
                        />
                        <div className={`p-2 rounded-full border transition-colors ${formData.isFavorite ? 'bg-yellow-50 border-yellow-200 text-yellow-500' : 'bg-gray-50 border-gray-200 text-gray-400'}`}>
                            <SafeIcon icon={FiStar} className={`w-5 h-5 ${formData.isFavorite ? 'fill-current' : ''}`} />
                        </div>
                        <span className="text-sm font-medium text-gray-700">Favorit</span>
                    </label>
                </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zutaten (kommagetrennt)</label>
              <textarea
                value={formData.ingredients}
                onChange={e => setFormData({ ...formData, ingredients: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20"
                placeholder="Nudeln, Hackfleisch, Tomaten..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rezept Link</label>
              <input
                type="url"
                value={formData.recipeUrl}
                onChange={e => setFormData({ ...formData, recipeUrl: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="https://chefkoch.de/..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}